import React, { createContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { io } from "socket.io-client";

const AppContext = createContext();
const API_URL = "https://api.menup.app.br";
const APP_URL = "https://app.olympicbarbacena.com.br";

export const AppProvider = ({ children }) => {
  const [state, setState] = useState(() => {
    // Estado inicial sincronizado com localStorage
    const token = localStorage.getItem("token");
    const user = JSON.parse(localStorage.getItem("user"));
    
    return {
      user,
      token,
      isAuthenticated: !!token,
      associados: [],
      loading: false,
      error: null,
      socket: null,
      onlineUsers: [],
      initialized: false,
      realTimeSaldo: null,
      socketConnected: false
    };
  });

  // Validar token ao inicializar
  useEffect(() => {
    const validateToken = async () => {
      if (state.token && !state.initialized) {
        try {
          const response = await axios.get(`${APP_URL}/api/auth/validate`, {
            headers: { Authorization: `Bearer ${state.token}` }
          });

          // Atualiza state mantendo outros valores
          setState(prev => ({
            ...prev,
            user: response.data.data.user,
            isAuthenticated: true,
            initialized: true,
            realTimeSaldo: response.data.data.user?.saldo || null
          }));

        } catch (error) {
          // Token inválido - limpa tudo
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          setState(prev => ({
            ...prev,
            user: null,
            token: null,
            isAuthenticated: false,
            initialized: true,
            realTimeSaldo: null
          }));
        }
      } else {
        setState(prev => ({ ...prev, initialized: true }));
      }
    };

    validateToken();
  }, [state.token]);

  // Configurar headers do axios
  useEffect(() => {
    if (state.token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${state.token}`;
      localStorage.setItem("token", state.token);
      if (state.user) {
        localStorage.setItem("user", JSON.stringify(state.user));
      }
    } else {
      delete axios.defaults.headers.common["Authorization"];
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    }
  }, [state.token, state.user]);

  // Inicializar conexão Socket.io
  useEffect(() => {
    if (state.token && state.user && !state.socket) {
      console.log('🔌 Iniciando conexão Socket.io...');
      
      const socket = io(APP_URL, {
        auth: { 
          token: state.token 
        },
        transports: ['websocket', 'polling']
      });

      // Eventos do Socket.io
      socket.on("connect", () => {
        console.log("✅ Socket conectado");
        setState(prev => ({ ...prev, socketConnected: true }));
      });

      socket.on("disconnect", (reason) => {
        console.log("❌ Socket desconectado:", reason);
        setState(prev => ({ ...prev, socketConnected: false }));
      });

      socket.on("connect_error", (error) => {
        console.error("❌ Erro de conexão Socket:", error);
        setState(prev => ({ ...prev, socketConnected: false }));
      });

      // Atualização de saldo em tempo real
      socket.on("saldo:update", (data) => {
        console.log("💰 Saldo atualizado em tempo real:", data);
        
        setState(prev => ({
          ...prev,
          realTimeSaldo: data.saldo,
          user: prev.user ? {
            ...prev.user,
            saldo: data.saldo
          } : null
        }));
      });

      // Novos pedidos
      socket.on("order:new", (data) => {
        console.log("🆕 Novo pedido recebido:", data);
        // Aqui você pode adicionar notificações ou outras ações
      });

      // Atualização de status do pedido
      socket.on("order:status-update", (data) => {
        console.log("📦 Status do pedido atualizado:", data);
        // Atualizar status dos pedidos na UI se necessário
      });

      // Usuários online
      socket.on("updateOnlineUsers", (users) => {
        setState(prev => ({ ...prev, onlineUsers: users }));
      });

      setState(prev => ({ ...prev, socket }));

      return () => {
        console.log('🔌 Limpando conexão Socket.io');
        socket.disconnect();
      };
    }
  }, [state.token, state.user]);

  // Funções de autenticação
  const login = async (credentials) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const response = await axios.post(`${APP_URL}/api/auth/login`, credentials);
      console.log('🔐 Resposta completa do login:', response.data);
      
      const userData = response.data.data.user;
      
      // Update state with user and token
      setState(prev => ({
        ...prev,
        user: userData,
        token: response.data.token,
        realTimeSaldo: userData?.saldo || null,
        loading: false,
        isAuthenticated: true
      }));

      return response.data;
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error.response?.data?.message || "Login failed",
        loading: false
      }));
      throw error;
    }
  };

  const logout = () => {
    console.log('🚪 Realizando logout...');
    if (state.socket) {
      state.socket.disconnect();
    }
    
    setState({
      user: null,
      token: null,
      associados: [],
      loading: false,
      error: null,
      socket: null,
      onlineUsers: [],
      initialized: true,
      realTimeSaldo: null,
      socketConnected: false
    });
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${APP_URL}/api/auth/me`);
      const userData = response.data.user || response.data.data?.user;
      
      setState(prev => ({
        ...prev,
        user: userData,
        realTimeSaldo: userData?.saldo || null
      }));
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      logout();
    }
  };

  // Solicitar saldo atual via socket
  const refreshSaldo = useCallback(() => {
    if (state.socket && state.socketConnected) {
      state.socket.emit('saldo:get');
    } else {
      // Fallback: buscar via API
      refreshUser();
    }
  }, [state.socket, state.socketConnected]);

  // Atualizar saldo manualmente
  const updateSaldo = useCallback((newSaldo) => {
    setState(prev => ({
      ...prev,
      realTimeSaldo: newSaldo,
      user: prev.user ? {
        ...prev.user,
        saldo: newSaldo
      } : null
    }));
  }, []);

  // Funções de busca de dados
  const fetchAssociados = useCallback(async (nome) => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      const response = await axios.get(`${API_URL}/api/associado`, {
        params: { page: 1, limit: 2000, nome },
        headers: { 
          'Authorization': `Bearer ${state.token}`
        }
      });
      setState(prev => ({ ...prev, associados: response.data.data }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error.response?.data?.message || "Failed to load data"
      }));
      if (error.response?.status === 401) logout();
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [state.token]);

  const fetchDependentes = async (id) => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      const response = await axios.get(`${API_URL}/api/dependente`, {
        params: { id },
        headers: { 
          'Authorization': `Bearer ${state.token}`
        }
      });
      return response.data;
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error.response?.data?.message || "Failed to load dependents"
      }));
      throw error;
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  // Auto-login on mount if token exists
  useEffect(() => {
    if (state.token && !state.user && state.initialized) {
      refreshUser();
      fetchAssociados();
    }
  }, [state.token, state.user, state.initialized, fetchAssociados]);

  // Obter saldo atual (prioriza tempo real, fallback para user.saldo)
  const getCurrentSaldo = useCallback(() => {
    return state.realTimeSaldo !== null ? state.realTimeSaldo : state.user?.saldo;
  }, [state.realTimeSaldo, state.user]);

  // Context value
  const contextValue = {
    ...state,
    login,
    logout,
    refreshUser,
    refreshSaldo,
    updateSaldo,
    getCurrentSaldo,
    fetchAssociados,
    fetchDependentes,
    isAuthenticated: !!state.token && !!state.user,
    hasRole: (role) => state.user?.role === role,
    isAdmin: state.user?.role === "admin",
    isStaff: state.user?.role === "staff",
    isMember: state.user?.role === "member"
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook for easier consumption
export const useAppContext = () => {
  const context = React.useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};

export default AppContext;